import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import PageLayout from "components/layout/PageLayout"
import NoData from "components/commons/NoData"
import { useLazyGetDomusGatewaysQuery } from "./api/domusApiSlice"
import DomusGatewayCard from "./views/DomusGatewayCard"

const REFRESH_SECONDS = 60

const DomusDashboard = () => {
    const [t] = useTranslation(["domus"])
    const [autoRefresh, setAutoRefresh] = useState(true)

    const [getGateways, {
        data: gateways,
        isLoading,
        isSuccess
    }] = useLazyGetDomusGatewaysQuery()

    useEffect(() => {
        getGateways()
        if (!autoRefresh) return
        const interval = setInterval(() => getGateways(), REFRESH_SECONDS * 1000)
        return () => clearInterval(interval)
    }, [getGateways, autoRefresh])

    const groupedGateways = useMemo(() => {
        if (!gateways) return []
        const map = new Map()
        gateways.forEach(gw => {
            const key = gw.businessUnit?.guid ?? "__unassigned__"
            if (!map.has(key)) {
                map.set(key, {
                    guid: key,
                    name: gw.businessUnit?.name ?? t("domus:helper.unassigned_bu"),
                    gateways: []
                })
            }
            map.get(key).gateways.push(gw)
        })
        // assigned BUs first, unassigned last
        return [...map.values()].sort((a, b) => {
            if (a.guid === "__unassigned__") return 1
            if (b.guid === "__unassigned__") return -1
            return a.name.localeCompare(b.name)
        })
    }, [gateways, t])

    const content = (
        <>
            <div className="d-flex justify-content-end mb-3 form-check form-switch">
                <input
                    type="checkbox"
                    className="form-check-input"
                    checked={autoRefresh}
                    onChange={() => setAutoRefresh(v => !v)}
                />
                <label className="form-check-label ms-2">
                    Auto-Refresh <span className="text-muted">{REFRESH_SECONDS}s</span>
                </label>
            </div>

            {isSuccess && gateways.length === 0 && (
                <NoData message={t("domus:helper.no_gateways")} />
            )}

            {isSuccess && groupedGateways.map(group => (
                <div key={group.guid} className="mb-5">
                    <div className="row align-items-center mb-3">
                        <h5 className="fw-bold col-auto mb-0">
                            <i className="fa-solid fa-building me-2 text-muted" />
                            {group.name}
                        </h5>
                        <hr className="col" />
                        <span className="col-auto text-muted" style={{ fontSize: "0.8rem" }}>
                            {group.gateways.length} gateway{group.gateways.length !== 1 ? "s" : ""}
                        </span>
                    </div>
                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                        {group.gateways.map(gw => (
                            <DomusGatewayCard key={gw.macAddress} gateway={gw} />
                        ))}
                    </div>
                </div>
            ))}
        </>
    )

    return (
        <PageLayout
            pageTitle={t("domus:menu.ambient_dashboard")}
            isLoadingPage={isLoading}
            onPageRefresh={getGateways}
            bodyContent={content}
        />
    )
}

export default DomusDashboard
